// src/pages/auths/UserManagement.tsx
import React, {useState, useEffect } from 'react';
import {collection, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import {db } from '../../firebase';
import {getAuth } from 'firebase/auth';
import {
  AdminHeader,
  AdminTitle,
  AdminContent,
  UserTable,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  ActionButton,
  SearchBar,
  FilterContainer,
  PaginationContainer,
  StatsContainer,
  StatCard,
  LoadingSpinner,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalActions
} from './AdminDashboard.styles';

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  createdAt: Date;
  status: 'pending' | 'approved' | 'banned' | 'suspended';
  method: 'email' | 'phone';
  stallNumber: string;
  profileComplete: boolean;
}

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'ban' | 'suspend' | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    banned: 0,
    suspended: 0
  });

  const usersPerPage = 10;
  const auth = getAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const usersCollectionRef = collection(db, 'users');
        const unsubscribe = onSnapshot(usersCollectionRef, (usersSnapshot) => {
          const fetchedUsers: User[] = [];
          usersSnapshot.forEach((userDoc) => {
            const userData = userDoc.data();
            fetchedUsers.push({
              id: userDoc.id,
              name: userData.name || 'N/A',
              email: userData.email || 'N/A',
              phoneNumber: userData.phoneNumber || 'N/A',
              createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(),
              status: userData.status || 'pending',
              method: userData.method || (userData.email ? 'email' : 'phone'),
              stallNumber: userData.stallNumber || 'N/A',
              profileComplete: userData.profileComplete || false
            });
          });
          setUsers(fetchedUsers);
          updateStats(fetchedUsers);
          setLoading(false);
        }, (error) => {
          console.error("Error setting up users collection listener:", error);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error initiating user fetch process:", error);
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        (user.phoneNumber && user.phoneNumber.toLowerCase().includes(term)) ||
        user.stallNumber.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(result);
    setCurrentPage(1);
  }, [users, searchTerm, statusFilter]);

  const updateStats = (usersList: User[]) => {
    const stats = {
      total: usersList.length,
      pending: usersList.filter(u => u.status === 'pending').length,
      approved: usersList.filter(u => u.status === 'approved').length,
      banned: usersList.filter(u => u.status === 'banned').length,
      suspended: usersList.filter(u => u.status === 'suspended').length
    };
    setStats(stats);
  };

  const handleStatusChange = async (newStatus: 'approved' | 'banned' | 'suspended') => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const userDocRef = doc(db, 'users', selectedUser.id);

      await updateDoc(userDocRef, {
        status: newStatus,
        reviewedAt: new Date(),
        reviewedBy: auth.currentUser?.uid || 'admin'
      });

      setShowModal(false);
      setSelectedUser(null);
      setActionType(null);
    } catch (error) {
      console.error("Error updating user status:", error);
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (user: User, action: 'approve' | 'ban' | 'suspend') => {
    setSelectedUser(user);
    setActionType(action);
    setShowModal(true);
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <>
      <AdminHeader>
        <AdminTitle>User Verification Dashboard</AdminTitle>
      </AdminHeader>
      <StatsContainer>
        <StatCard>
          <h3>Total Users</h3>
          <p>{stats.total}</p>
        </StatCard>
        <StatCard className="pending">
          <h3>Pending</h3>
          <p>{stats.pending}</p>
        </StatCard>
        <StatCard className="approved">
          <h3>Approved</h3>
          <p>{stats.approved}</p>
        </StatCard>
        <StatCard className="banned">
          <h3>Banned</h3>
          <p>{stats.banned}</p>
        </StatCard>
        <StatCard className="suspended">
          <h3>Suspended</h3>
          <p>{stats.suspended}</p>
        </StatCard>
      </StatsContainer>

      <FilterContainer>
        <SearchBar
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="banned">Banned</option>
          <option value="suspended">Suspended</option>
        </select>
      </FilterContainer>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <AdminContent>
           <UserTable>
        <thead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Email/Phone</TableHeader>
            <TableHeader>Stall Number</TableHeader>
            <TableHeader>Signup Method</TableHeader>
            <TableHeader>Created At</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </thead>
        <tbody>
          {currentUsers.length > 0 ? (
            currentUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.method === 'email' ? user.email : user.phoneNumber}</TableCell>
                <TableCell>{user.stallNumber}</TableCell>
                <TableCell>{user.method === 'email' ? 'Email' : 'Phone'}</TableCell>
                <TableCell>{user.createdAt.toLocaleDateString()}</TableCell>
                <TableCell>
                  <StatusBadge status={user.status}>
                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  {user.status === 'pending' && (
                    <>
                      <ActionButton
                        onClick={() => openActionModal(user, 'approve')}
                        className="approve"
                      >
                        Approve
                      </ActionButton>
                      <ActionButton
                        onClick={() => openActionModal(user, 'ban')}
                        className="ban"
                      >
                        Ban
                      </ActionButton>
                    </>
                  )}
                  {user.status === 'approved' && (
                    <ActionButton
                      onClick={() => openActionModal(user, 'suspend')}
                      className="suspend"
                    >
                      Suspend
                    </ActionButton>
                  )}
                  {(user.status === 'banned' || user.status === 'suspended') && (
                    <ActionButton
                      onClick={() => openActionModal(user, 'approve')}
                      className="approve"
                    >
                      Approve
                    </ActionButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} style={{textAlign: 'center' }}>
                No users found matching your criteria
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </UserTable>

          {totalPages > 1 && (
            <PaginationContainer>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </PaginationContainer>
          )}
        </AdminContent>
      )}

      {showModal && selectedUser && actionType && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>
                {actionType === 'approve' && 'Approve User'}
                {actionType === 'ban' && 'Ban User'}
                {actionType === 'suspend' && 'Suspend User'}
              </h3>
            </ModalHeader>
            <p>
              Are you sure you want to {actionType} {selectedUser.name}?
              {actionType === 'ban' && ' This will permanently restrict them from accessing the platform.'}
              {actionType === 'suspend' && ' This will temporarily disable their account.'}
            </p>
            <ModalActions>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedUser(null);
                  setActionType(null);
                }}
                className="cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusChange(
                  actionType === 'approve' ? 'approved' :
                  actionType === 'ban' ? 'banned' : 'suspended'
                )}
                className={actionType}
                disabled={loading}
              >
                {loading ? 'Processing...' : `Confirm ${actionType}`}
              </button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default UserManagement;